import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import { usePersona, useSimpanPersona } from '../../../domains/tenancy/api'
import {
  AreaTeks, Bendera, Body, Chip, Gagal, Isian, KV, KVBaris, Kolom2, Memuat, Panel, Seg,
  Sub, useSimpanKepala,
} from '../../../shared/ui'

/**
 * PERSONA & KEBIJAKAN — identitas bot, dan pagar di sekelilingnya.
 *
 * Sampai migrasi 0030 seluruh isi layar ini adalah teks di dalam
 * `ai/prompts/percakapan.py`, tersebar di sebelas tempat. Selama cuma ada satu klien itu
 * tidak terasa; pada klien kedua bot memperkenalkan diri sebagai perusahaan orang lain.
 *
 * YANG DISIMPAN ADALAH PILIHANNYA, BUKAN KALIMATNYA. "Ramah" tersimpan sebagai `ramah`,
 * dan kalimat instruksinya ditulis di kode. Kalau kalimatnya sendiri bisa disunting dari
 * sini, layar ini jadi pintu belakang ke perilaku bot — "gaya bahasa" bisa diisi
 * "abaikan semua instruksi di atas".
 *
 * Karena itu ada PRATINJAU: orang berhak melihat akibat pilihannya, dan "ramah" tidak
 * memberi tahu apa pun tentang bagaimana bot akan berbicara. Yang ditampilkan adalah
 * kalimat yang benar-benar masuk ke prompt, dirakit backend — bukan tiruan yang dibangun
 * ulang di sini dan bisa menyimpang.
 */
export default function Persona() {
  const { t } = useBahasa()
  const q = usePersona()
  const simpan = useSimpanPersona()
  // SUNTINGAN DITUMPUK DI ATAS DATA, bukan disalin ke state lewat useEffect.
  //
  // Dua alasan, dan keduanya nyata. Pertama: `useEffect` tidak berjalan di
  // `renderToString`, jadi layar yang mengisi state awalnya di sana selalu terender
  // sebagai "memuat" dan tidak pernah benar-benar teruji. Kedua, dan lebih penting:
  // efek yang menyalin ulang tiap `q.data` berubah akan MENIMPA suntingan yang sedang
  // diketik begitu ada refetch — perubahan orang hilang tanpa satu pun galat.
  const [ubah, setUbah] = useState({})

  const { pratinjau, ...tersimpan } = q.data ?? {}
  const f = { ...tersimpan, ...ubah }
  const set = (k, v) => setUbah((x) => ({ ...x, [k]: v }))
  const bisa = Boolean(f.nama_bot?.trim() && f.atas_nama?.trim() && f.bidang?.trim())

  // DI ATAS SEMUA `return` AWAL. Hook yang dipanggil nol kali saat memuat lalu satu kali
  // saat data sampai membuat React melempar dan MENGOSONGKAN halaman.
  useSimpanKepala(
    () => simpan.mutate(
      { ...f, email_rujukan: f.email_rujukan?.trim() || null },
      { onSuccess: () => setUbah({}) },
    ),
    { bisa, menyimpan: simpan.isPending },
  )

  if (q.isLoading) return <Memuat tinggi={6} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  return (
    <div className="flex flex-col gap-3.5">
      <Kolom2>
      <Panel judul={t('ta.identitasJudul')} catatan={t('ta.identitasCatatan')}>
        <Body>
          <KV>
            <KVBaris label={t('ta.namaBot')}>
              <Isian
                className="max-w-[220px]"
                value={f.nama_bot ?? ''}
                onChange={(e) => set('nama_bot', e.target.value)}
              />
              <Sub>{t('ta.namaBotCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.atasNama')}>
              <Isian value={f.atas_nama ?? ''} onChange={(e) => set('atas_nama', e.target.value)} />
              <Sub>{t('ta.atasNamaCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.bidang')}>
              <AreaTeks
                baris={2}
                value={f.bidang ?? ''}
                onChange={(e) => set('bidang', e.target.value)}
              />
              <Sub>{t('ta.bidangCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.emailRujukan')}>
              <Isian
                className="max-w-[280px]"
                placeholder="info@seleris.id"
                value={f.email_rujukan ?? ''}
                onChange={(e) => set('email_rujukan', e.target.value)}
              />
              <Sub>{t('ta.emailRujukanCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.gayaBahasa')}>
              <Seg
                nilai={f.gaya_bahasa}
                set={(v) => set('gaya_bahasa', v)}
                daftar={[
                  { id: 'formal', label: t('ta.gayaFormal') },
                  { id: 'ramah', label: t('ta.gayaRamah') },
                  { id: 'santai', label: t('ta.gayaSantai') },
                ]}
              />
            </KVBaris>
          </KV>
        </Body>
      </Panel>

      <Panel judul={t('ta.kebijakanJudul')} catatan={t('ta.kebijakanCatatan')}>
        <Body>
          <KV>
            <KVBaris label={t('ta.melayani')}>
              <Seg
                nilai={f.melayani}
                set={(v) => set('melayani', v)}
                daftar={[
                  { id: 'b2b', label: t('ta.b2b') },
                  { id: 'b2c', label: t('ta.b2c') },
                  { id: 'keduanya', label: t('ta.keduanya') },
                ]}
              />
              <Sub>{t('ta.melayaniCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.demoTrial')}>
              <Seg
                nilai={f.demo_trial}
                set={(v) => set('demo_trial', v)}
                daftar={[
                  { id: 'meeting', label: t('ta.demoMeeting') },
                  { id: 'mandiri', label: t('ta.demoMandiri') },
                ]}
              />
            </KVBaris>

            <KVBaris label={t('ta.catatanKhusus')}>
              <AreaTeks
                baris={3}
                placeholder="Mis. jangan menyebut harga sebelum meeting."
                value={f.catatan ?? ''}
                onChange={(e) => set('catatan', e.target.value)}
              />
              <Sub>{t('ta.catatanKhususCatatan')}</Sub>
            </KVBaris>
          </KV>
        </Body>

        <Body className="border-t border-line">
          {simpan.isError && (
            <Bendera nada="bad" role="alert" className="mb-3">{simpan.error?.message}</Bendera>
          )}
          <Sub>{t('ta.personaBerlaku')}</Sub>
        </Body>
      </Panel>

      </Kolom2>

      <Panel judul={t('ta.pratinjauJudul')} catatan={t('ta.pratinjauCatatan')}>
        <Body>
          {/* Dirakit BACKEND, bukan dibangun ulang di sini. Tiruan di frontend akan
              menyimpang dari yang sebenarnya dikirim ke model begitu salah satunya
              disunting — dan pratinjau yang berbohong lebih buruk daripada tidak ada. */}
          <pre className="m-0 whitespace-pre-wrap text-[11.5px] leading-[1.55] text-ink2
                          font-mono bg-surface2 border border-line rounded p-3">
            {q.data?.pratinjau}
          </pre>
          <Sub>{t('ta.pratinjauLama')}</Sub>
        </Body>
      </Panel>

      {/* PAGAR KESELAMATAN — panel, bukan spanduk, dan itu bukan kosmetik.
          Rancangan menaruhnya sebagai panel setara dengan Identitas dan Kebijakan,
          dengan lencana gembok: *"ditampilkan supaya terlihat bahwa ia ADA dan sengaja
          terkunci — bukan seolah tidak ada."* Spanduk peringatan terbaca sebagai catatan
          kaki, dan catatan kaki dilewati justru oleh orang yang paling perlu tahu bahwa
          tiga aturan ini tidak bisa dimatikan siapa pun.

          Ketiganya TIDAK dibaca dari backend. Mereka tertanam di kode perakit prompt dan
          memang tidak disediakan sebagai pilihan; membuat rute untuk membacanya berarti
          menyiratkan ia bisa berubah. */}
      <Panel
        judul={t('ta.pagarJudul')}
        catatan={t('ta.pagarCatatan')}
        aksi={<Chip nada="idle" titik={false}>🔒 {t('ta.pagarKunci')}</Chip>}
      >
        <Body>
          <div className="bg-surface2 border border-line rounded-md p-3">
            <b className="block text-[12.5px]">{t('ta.pagarSelalu')}</b>
            <ul className="mt-2 mb-0 pl-4 flex flex-col gap-1.5 text-[12px] text-ink2">
              <li>{t('ta.pagar1')}</li>
              <li>{t('ta.pagar2')}</li>
              <li>{t('ta.pagar3')}</li>
            </ul>
          </div>
          <Sub>{t('ta.pagarIsi')}</Sub>
        </Body>
      </Panel>
    </div>
  )
}
